<template>
    <div v-if="model">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            :validations="validations.unidade"
                            @change="handleUnidadeChange"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.infraestruturas"
                            :validations="validations.infraestrutura"
                            @change="handleInfraestruturaChange"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.especialidades"
                            @change="handleEspecialidadeChange"
                            default-option-text="Todas"
                            label="Especialidade"
                            show-default-option
                            text-field="nome"
                            v-model="model.especialidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.usuarios"
                            @change="handleUsuarioChange"
                            default-option-text="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.status"
                            default-option-text="Todos"
                            label="Status"
                            show-default-option
                            text-field="nome"
                            v-model="model.statusId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <template v-if="data">
            <rkm-area
                no-margin-top
                title="Resultado"
            >
                <template v-slot:trailing>
                    <rkm-button
                        @click="print"
                        color="info"
                        small
                        title="Imprimir"
                    >
                        <rkm-icon icon="print" />
                    </rkm-button>

                    <rkm-button
                        @click="exportTo"
                        color="info"
                        small
                        title="Exportar"
                    >
                        <rkm-icon icon="file-export" />
                    </rkm-button>
                </template>

                <rkm-table
                    :fields="fields"
                    :items="data.atendimentos"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    @sort-changed="onSortChanged"
                    hide-filters
                >
                    <template v-slot:cell(procedimentos)="{item}">
                        <div
                            v-for="(procedimento, index) in item.procedimentos"
                            :key="index"
                        >
                            {{procedimento}} <br/>
                        </div>
                    </template>

                    <template v-slot:cell(cidsCiaps)="{item}">
                        <div
                            v-for="(cidCiap, index) in item.cidsCiaps"
                            :key="index"
                        >
                            {{cidCiap}} <br/>
                        </div>
                    </template>
                </rkm-table>
            </rkm-area>

            <rkm-area
                no-margin-top
                title="Totais"
                type="fieldset"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadePacientes"
                            disabled
                            label="Total de pacientes"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadeAtendimentosRealizados"
                            disabled
                            label="Total de atendimentos realizadas"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadeFaltas"
                            disabled
                            label="Total de faltas"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="data.totalizadores.quantidadeProcedimentosLancados"
                            disabled
                            label="Total de procedimentos lançados"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </template>
    </div>
</template>

<script>
// Mixins.
import {error, file, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        validations() {
            return {
                periodo: {required: true},
                unidade: {required: true},
                infraestrutura: {required: true},
            }
        },
    },
    created() {
        this.repositories = {
            exportacao: this.getConectaSusRepository('exportacao.ambulatorio'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
            relatorioConsultaFinalizada: this.getConectaSusRepository('ambulatorio.relatorioConsultaFinalizada'),
        }

        this.load()
    },
    data() {
        return {
            data: null,
            fields: [
                {key: 'demandaTipo', label: 'Tipo de demanda', sortable: true},
                {key: 'dataAtendimento', label: 'Data de atendimento', sortable: true},
                {key: 'usuarioNome', label: 'Profissional', sortable: true},
                {key: 'especialidadeNome', label: 'Especialidade', sortable: true},
                {key: 'pacienteNome', label: 'Paciente', sortable: true},
                {key: 'pacienteProntuario', label: 'Prontuário', sortable: true},
                {key: 'horarioAtendimento', label: 'Horário', sortable: true},
                {key: 'cidsCiaps', label: 'Cids / Ciaps'},
                {key: 'procedimentos', label: 'Procedimentos'},
                {key: 'agendamentoStatusNome', label: 'Status', sortable: true},
            ],
            form: null,
            model: null,
            others: {moment},
            sortBy: 'demandaTipo',
            sortDesc: false,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioConsultaFinalizada.create()
                .then((response) => {
                    this.form = response.data.form
                    this.model = response.data.model
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            const request = {
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                especialidadeId: this.model.especialidadeId,
                usuarioId: this.model.usuarioId,
                statusId: this.model.statusId,
            }

            this.setLoading()

            this.repositories.relatorioConsultaFinalizada.index(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.data = Object.assign(response.data))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.especialidades = []
            this.form.usuarios = []
            this.form.status = []

            this.model.periodo = {
                start: null,
                end: null,
            }

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.especialidadeId = null
            this.model.usuarioId = null
            this.model.statusId = null

            this.data = null

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {
            this.setLoading()

            const request = {
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                especialidadeId: this.model.especialidadeId,
                usuarioId: this.model.usuarioId,
                statusId: this.model.statusId,
                sortBy: this.sortBy ? this.camelToSnake(this.sortBy) : null,
                sortDesc: this.sortDesc,
            }

            this.repositories.impressao.relatorioConsultaFinalizada(this.camelObjectKeysToSnakeRecursive(request))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            const request = {
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                especialidadeId: this.model.especialidadeId,
                usuarioId: this.model.usuarioId,
                statusId: this.model.statusId,
                sortBy: this.sortBy ? this.camelToSnake(this.sortBy) : null,
                sortDesc: this.sortDesc,
            }

            this.repositories.exportacao.relatorioConsultaFinalizada(this.camelObjectKeysToSnakeRecursive(request))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        handleUnidadeChange(value) {
            this.form.infraestruturas = []
            this.form.especialidades = []
            this.form.usuarios = []
            this.form.status = []
            this.model.infraestruturaId = null
            this.model.especialidadeId = null
            this.model.usuarioId = null
            this.model.statusId = null

            if (value === null) {
                return
            }

            this.getInfraestruturas()
        },
        handleInfraestruturaChange(value) {
            this.form.especialidades = []
            this.form.usuarios = []
            this.form.status = []
            this.model.especialidadeId = null
            this.model.usuarioId = null
            this.model.statusId = null

            if (value === null) {
                return
            }

            this.getEspecialidades()
        },
        handleEspecialidadeChange() {
            this.form.usuarios = []
            this.form.status = []
            this.model.usuarioId = null
            this.model.statusId = null

            this.getUsuarios()
        },
        handleUsuarioChange() {
            this.form.status = []
            this.model.statusId = null

            this.getStatus()
        },
        getUnidades() {
            const request = {
                listar: 'unidades',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
            }

            this.setLoading()

            this.repositories.relatorioConsultaFinalizada.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.form.unidades = Object.assign(response.data.unidades))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getInfraestruturas() {
            const request = {
                listar: 'infraestruturas',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
            }

            this.setLoading()

            this.repositories.relatorioConsultaFinalizada.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.infraestruturas = Object.assign(response.data.infraestruturas)

                    if (this.form.infraestruturas.length === 1) {
                        this.model.infraestruturaId = this.form.infraestruturas[0].id

                        this.getEspecialidades()
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getEspecialidades() {
            const request = {
                listar: 'especialidades',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
            }

            this.setLoading()

            this.repositories.relatorioConsultaFinalizada.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.especialidades = Object.assign(response.data.especialidades)
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getUsuarios() {
            const request = {
                listar: 'usuarios',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                especialidadeId: this.model.especialidadeId,
            }

            this.setLoading()

            this.repositories.relatorioConsultaFinalizada.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getStatus() {
            const request = {
                listar: 'status',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                especialidadeId: this.model.especialidadeId,
                usuarioId: this.model.usuarioId,
            }

            this.setLoading()

            this.repositories.relatorioConsultaFinalizada.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.form.status = Object.assign(response.data.status))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
    },
    mixins: [error, file, repository, stringCase],
    watch: {
        'model.periodo': {
            deep: true,
            handler(value) {
                this.form.unidades = []
                this.form.infraestruturas = []
                this.form.especialidades = []
                this.form.usuarios = []
                this.form.status = []
                this.model.unidadeId = null
                this.model.infraestruturaId = null
                this.model.especialidadeId = null
                this.model.usuarioId = null
                this.model.statusId = null

                if (value.start === null || value.end === null)  {
                    return
                }

                this.getUnidades()
            },
        },
    },
}
</script>